import { DateTime } from 'luxon'
import { useQueryClient } from 'react-query'
import { Membership, SessionsApi, SuccessResponse } from '@citruscamps/citrus-client'
import { useToast } from '../../../../../hooks/useToast'
import translations from '../../../../../translations/en.json'
import { generateApiConfig } from '../../../../../utils/client-config'
import { generateBaseKey } from '../../../../../utils/key-generator'
import { useRequestHandler } from '../../../../../hooks/useRequestHandler'

interface IProps {
  programId?: string
}

interface IUpdateMemberships {
  handleCancel: (membership: Membership) => Promise<void>
  handleUndoCancel: (membership: Membership) => Promise<void>
}

export const useUpdateMemberships = ({ programId }: IProps): IUpdateMemberships => {
  const { requestHandler } = useRequestHandler()
  const queryClient = useQueryClient()
  const { setToast } = useToast()

  const handleCancel = async ({ program_attendee, package: pack, ...value }: Membership) => {
    if (programId && value.id && value.cancel_at) {
      const today = DateTime.local().startOf('day')
      const cancelDate = DateTime.fromISO(value.cancel_at)
      const membershipClient = new SessionsApi(generateApiConfig())
      if (today.hasSame(cancelDate, 'day')) {
        await requestHandler<SuccessResponse>(
          () =>
            membershipClient.cancelMembership({ program_id: programId, membership_id: value.id }),
          {
            toast: {
              setToast,
            },
          },
        )
      } else {
        await requestHandler<Membership>(
          () =>
            membershipClient.updateMembership({
              program_id: programId,
              membership_id: value.id,
              MembershipUpdateParams: {
                ...value,
                program_attendee_id: program_attendee.id,
                package_id: pack.id,
              },
            }),
          {
            toast: {
              setToast,
            },
          },
        )
      }
      await queryClient.invalidateQueries(
        generateBaseKey({
          type: 'membership',
          program_id: programId,
        }),
      )
    } else {
      setToast({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  const handleUndoCancel = async ({ program_attendee, package: pack, ...value }: Membership) => {
    if (programId && value.id && value.cancel_at) {
      const membershipClient = new SessionsApi(generateApiConfig())
      await requestHandler<Membership>(
        () =>
          membershipClient.updateMembership({
            program_id: programId,
            membership_id: value.id,
            MembershipUpdateParams: {
              ...value,
              program_attendee_id: program_attendee.id,
              package_id: pack.id,
              cancel_at: undefined,
              cancel_at_period_end: false,
            },
          }),
        {
          toast: {
            setToast,
          },
        },
      )
      await queryClient.invalidateQueries(
        generateBaseKey({
          type: 'membership',
          program_id: programId,
        }),
      )
    } else {
      setToast({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  return { handleCancel, handleUndoCancel }
}
